import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Send, Globe } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

const XIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const GithubIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
);

const footerLinks = {
  Platform: [
    { label: 'Home', href: '/homepage' },
    { label: 'Beginner Guide', href: '/beginner-guide' },
    { label: 'Live Market', href: '/live-market-dashboard' },
    { label: 'Airdrop Hub', href: '/airdrop-hub' },
    { label: 'Submit Project', href: '/project-submission' },
  ],
  Learn: [
    { label: 'What is Crypto?', href: '/beginner-guide#what-is-crypto' },
    { label: 'What is Blockchain?', href: '/beginner-guide#blockchain' },
    { label: 'What is DeFi?', href: '/beginner-guide#defi' },
    { label: 'What is an Airdrop?', href: '/beginner-guide#airdrops' },
    { label: 'Crypto Safety Tips', href: '/beginner-guide#safety' },
  ],
  Company: [
    { label: 'About AIDARAM', href: '/homepage#about' },
    { label: 'DARAM Token', href: '/daram' },
    { label: 'Contact', href: '#' },
    { label: 'Data Sources', href: '#' },
  ],
  Legal: [
    { label: 'Terms of Service', href: '#' },
    { label: 'Privacy Policy', href: '#' },
    { label: 'Disclaimer', href: '#' },
    { label: 'Cookie Policy', href: '#' },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-[#07111F] border-t border-[rgba(22,194,255,0.1)] mt-20">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <AppLogo size={32} />
              <span className="font-sora font-bold text-lg text-[#E6F1FF]">AIDARAM</span>
            </div>
            <p className="text-sm text-[#93A4B8] leading-relaxed mb-1 font-dm">
              Artificial Intelligence and Digital Asset Research and Management
            </p>
            <p className="text-xs text-[#93A4B8]/70 leading-relaxed mb-5 font-dm">
              Learn Crypto. Track Narratives. Discover Opportunities.
            </p>
            <p className="text-xs text-[#93A4B8]/60 leading-relaxed font-dm max-w-xs">
              AIDARAM is a digital asset knowledge and discovery platform built to make crypto easier to understand and explore.
            </p>
            {/* Socials */}
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: XIcon, label: 'Twitter', href: 'https://x.com/AIDARAM124432' },
                { icon: Send, label: 'Telegram', href: 'https://t.me/aidaramofficialgroup' },
                { icon: Globe, label: 'Website', href: 'https://aidaram.net' },
              ]?.map(({ icon: Icon, label, href }) => (
                <a
                  key={`social-${label}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-lg bg-[#0D1B2A] border border-[rgba(22,194,255,0.15)] flex items-center justify-center text-[#93A4B8] hover:text-[#16C2FF] hover:border-[rgba(22,194,255,0.4)] hover:bg-[rgba(22,194,255,0.06)] transition-all duration-200"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks)?.map(([section, links]) => (
            <div key={`footer-section-${section}`}>
              <h4 className="font-sora text-xs font-600 text-[#93A4B8] uppercase tracking-widest mb-4">
                {section}
              </h4>
              <ul className="space-y-2.5">
                {links?.map((link) => (
                  <li key={`footer-link-${link?.label}`}>
                    <Link
                      href={link?.href}
                      className="text-sm text-[#93A4B8] hover:text-[#16C2FF] transition-colors font-dm"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[rgba(22,194,255,0.08)] pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#93A4B8]/60 font-dm">
            © 2026 AIDARAM (aidaram.net). All rights reserved.
          </p>
          <p className="text-xs text-[#93A4B8]/50 font-dm text-center sm:text-right max-w-lg">
            AIDARAM does not provide financial advice. Crypto assets are highly volatile. Always do your own research. Airdrop rewards listed are provided by third-party projects.
          </p>
        </div>
      </div>
    </footer>
  );
}